import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import CharacterCounter from '../components/CharacterCounter';
import Footer from '../components/Footer';
import { loadState, saveState } from '../utils/stateManager';
import { generatePreview as generatePreviewData, generatePackage } from '../utils/bicepGenerator';
import './Page8Preview.css';

function Page8Preview() {
  const navigate = useNavigate();
  const [state, setState] = useState(loadState());
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentState = loadState();
    setState(currentState);
    generatePreview(currentState);
  }, []);

  const generatePreview = (currentState) => {
    const data = generatePreviewData(currentState);
    setPreviewData(data);
    setLoading(false);
  };

  const handleBack = () => {
    navigate('/page7');
  };

  const handleDownload = () => {
    if (!previewData || !previewData.allValid) {
      alert('Please fix validation errors before downloading');
      return;
    }
    const data = generatePackage(state);
    downloadFiles(data.files);
  };

  const downloadFiles = (files) => {
    Object.entries(files).forEach(([filename, content]) => {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  };

  const handleViewCode = () => {
    if (!previewData || !previewData.allValid) {
      alert('Please fix validation errors before viewing code');
      return;
    }
    const data = generatePackage(state);
    const currentState = loadState();
    saveState({ ...currentState, generatedFiles: data.files });
    navigate('/page9');
  };

  if (loading) {
    return <div className="page-container"><p>Generating preview...</p></div>;
  }

  if (!previewData) {
    return (
      <div className="page-container">
        <p>Unable to generate preview. Please go back and fill in the required fields.</p>
        <button className="btn-secondary" onClick={handleBack}>← Back</button>
      </div>
    );
  }

  const vmConfig = state.page3VMConfig || {};
  const discovery = state.page2Discovery || {};
  const useStorage = state.page5StorageAccount?.useStorageAccount || false;
  const useLAW = state.page6LogAnalytics?.useLogAnalytics || false;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Azure VM Deployment Wizard</h1>
        <ProgressBar currentPage={8} />
      </div>

      <div className="page-content">
        <div className={`validation-banner ${previewData.allValid ? 'valid' : 'invalid'}`}>
          {previewData.allValid ? '✅ All validation checks passed' : '❌ Validation errors found'}
        </div>

        <div className="config-summary">
          <h3>📋 Deployment Configuration</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="label">Environment:</span>
              <span className="value">{vmConfig.environment || 'N/A'}</span>
            </div>
            <div className="summary-item">
              <span className="label">Region:</span>
              <span className="value">{vmConfig.region || 'N/A'}</span>
            </div>
            <div className="summary-item">
              <span className="label">Subscription:</span>
              <span className="value">{discovery.subscription || 'N/A'}</span>
            </div>
            <div className="summary-item">
              <span className="label">Resource Group:</span>
              <span className="value">{discovery.resourceGroup || 'N/A'}</span>
            </div>
            <div className="summary-item">
              <span className="label">VNet:</span>
              <span className="value">{discovery.vnetName || 'N/A'}</span>
            </div>
            <div className="summary-item">
              <span className="label">Subnet:</span>
              <span className="value">{discovery.subnetName || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="resources-section">
          <h3>🏷️ Generated Resource Names</h3>

          {/* KeyVault - Always Required */}
          <div className="resource-card">
            <div className="resource-header">
              <span className={`status-icon ${previewData.resources.keyvault.valid ? 'valid' : 'invalid'}`}>
                {previewData.resources.keyvault.valid ? '✅' : '❌'}
              </span>
              <strong>KeyVault</strong>
              <span className="required-badge">Required</span>
            </div>
            <CharacterCounter
              value={previewData.resources.keyvault.name}
              maxLength={24}
            />
            <p className="resource-detail">Purpose: {previewData.resources.keyvault.purpose}</p>
            {!previewData.resources.keyvault.valid && previewData.resources.keyvault.errors && (
              <p className="error-message">{previewData.resources.keyvault.errors.join(', ')}</p>
            )}
          </div>

          {/* Storage Account - Optional */}
          {useStorage && previewData.resources.storage && (
            <div className="resource-card">
              <div className="resource-header">
                <span className={`status-icon ${previewData.resources.storage.valid ? 'valid' : 'invalid'}`}>
                  {previewData.resources.storage.valid ? '✅' : '❌'}
                </span>
                <strong>Storage Account</strong>
                <span className="optional-badge">Optional</span>
              </div>
              <CharacterCounter
                value={previewData.resources.storage.name}
                maxLength={24}
              />
              <p className="resource-detail">Purpose: {previewData.resources.storage.purpose}</p>
              {!previewData.resources.storage.valid && previewData.resources.storage.errors && (
                <p className="error-message">{previewData.resources.storage.errors.join(', ')}</p>
              )}
            </div>
          )}

          {/* Log Analytics - Optional */}
          {useLAW && previewData.resources.logAnalytics && (
            <div className="resource-card">
              <div className="resource-header">
                <span className={`status-icon ${previewData.resources.logAnalytics.valid ? 'valid' : 'invalid'}`}>
                  {previewData.resources.logAnalytics.valid ? '✅' : '❌'}
                </span>
                <strong>Log Analytics Workspace</strong>
                <span className="optional-badge">Optional</span>
              </div>
              <CharacterCounter
                value={previewData.resources.logAnalytics.name}
                maxLength={63}
              />
              <p className="resource-detail">Purpose: {previewData.resources.logAnalytics.purpose}</p>
            </div>
          )}

          {/* VM - Always Required */}
          <div className="resource-card">
            <div className="resource-header">
              <span className={`status-icon ${previewData.resources.vm.valid ? 'valid' : 'invalid'}`}>
                {previewData.resources.vm.valid ? '✅' : '❌'}
              </span>
              <strong>Virtual Machine</strong>
              <span className="required-badge">Required</span>
            </div>
            <CharacterCounter
              value={previewData.resources.vm.name}
              maxLength={15}
            />
            <p className="resource-detail">Size: {previewData.resources.vm.size}</p>
            <p className="resource-detail">Admin: {previewData.resources.vm.adminUser}</p>
          </div>

          {/* NIC - Always Required */}
          <div className="resource-card">
            <div className="resource-header">
              <span className="status-icon valid">✅</span>
              <strong>Network Interface</strong>
              <span className="required-badge">Required</span>
            </div>
            <p className="resource-name">{previewData.resources.nic.name}</p>
            <p className="resource-detail">Attached to: {discovery.subnetName}</p>
          </div>

          {/* OS Disk - Always Required */}
          <div className="resource-card">
            <div className="resource-header">
              <span className="status-icon valid">✅</span>
              <strong>OS Disk</strong>
              <span className="required-badge">Required</span>
            </div>
            <p className="resource-name">{previewData.resources.disk.name}</p>
            <p className="resource-detail">Type: Standard_LRS, 127GB</p>
          </div>
        </div>

        <div className="cost-section">
          <h3>💰 Estimated Monthly Cost</h3>
          <div className="cost-breakdown">
            <div className="cost-item">
              <span>VM ({previewData.resources.vm.size}):</span>
              <span>${previewData.costs.vm}</span>
            </div>
            <div className="cost-item">
              <span>Disk:</span>
              <span>${previewData.costs.disk}</span>
            </div>
            <div className="cost-item">
              <span>KeyVault:</span>
              <span>${previewData.costs.keyvault}</span>
            </div>
            {useStorage && (
              <div className="cost-item">
                <span>Storage Account:</span>
                <span>${previewData.costs.storage}</span>
              </div>
            )}
            {useLAW && (
              <div className="cost-item">
                <span>Log Analytics:</span>
                <span>${previewData.costs.logAnalytics}</span>
              </div>
            )}
            <div className="cost-total">
              <span>Total:</span>
              <span>~${previewData.costs.total}/month</span>
            </div>
          </div>
          <p className="cost-disclaimer">Estimates assume continuous operation. Actual costs vary.</p>
        </div>
      </div>

      <div className="page-actions">
        <button className="btn-secondary" onClick={handleBack}>
          ← Back
        </button>
        <button
          className="btn-primary"
          onClick={handleViewCode}
          disabled={!previewData.allValid}
        >
          View Code →
        </button>
        <button
          className="btn-primary"
          onClick={handleDownload}
          disabled={!previewData.allValid}
        >
          📥 Download Deployment Package
        </button>
      </div>

      <Footer />
    </div>
  );
}

export default Page8Preview;
